import React, { useEffect, useMemo, useState } from 'react'

type Field = { key: string; input_type: 'int'|'float'|'string'|'text' }

interface Props {
  isOpen: boolean
  onClose: () => void
  initialFields: Field[]
  onSave: (fields: Field[]) => Promise<void> | void
}

export default function EditTaskRequiredFieldsModal({ isOpen, onClose, initialFields, onSave }: Props) {
  const [fields, setFields] = useState<Field[]>([])
  const [newKey, setNewKey] = useState('')
  const [newType, setNewType] = useState<Field['input_type']>('string')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setFields((initialFields || []).map(f => ({ key: f.key, input_type: f.input_type })))
    setNewKey('')
    setNewType('string')
    setError(null)
  }, [isOpen, initialFields])

  if (!isOpen) return null

  const addField = () => {
    const raw = (newKey || '').trim()
    if (!raw) return
    const exists = fields.some(f => (f.key || '').trim().toLowerCase() === raw.toLowerCase())
    if (exists) { setError(`'${raw}' 항목은 이미 추가되어 있습니다.`); return }
    setFields(prev => [...prev, { key: raw, input_type: newType }])
    setNewKey('')
    setNewType('string')
    if (error) setError(null)
  }
  const removeField = (idx: number) => setFields(prev => prev.filter((_, i) => i !== idx))

  const handleSave = async () => {
    try {
      setSaving(true)
      await onSave(fields)
      onClose()
    } catch (e: any) {
      setError(e?.message || '저장 실패')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-900">핵심 입력 수정</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          <div className="grid grid-cols-4 gap-2">
            <input className="col-span-2 border rounded px-2 py-1 text-sm" placeholder="키 (예: Score)" value={newKey} onChange={e => setNewKey(e.target.value)} />
            <div className="flex gap-2 col-span-2">
              <select className="border rounded px-2 py-1 text-sm flex-1" value={newType} onChange={e => setNewType(e.target.value as Field['input_type'])}>
                <option value="int">int</option>
                <option value="float">float</option>
                <option value="string">string</option>
                <option value="text">text</option>
              </select>
              <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 shrink-0 whitespace-nowrap" onClick={addField}>추가</button>
            </div>
          </div>
          {error && <div className="text-xs text-red-600">{error}</div>}
          <div className="max-h-[260px] overflow-y-auto border rounded">
            {fields.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">항목이 없습니다. 위에서 추가하세요.</div>
            ) : (
              <ul className="divide-y">
                {fields.map((f, idx) => (
                  <li key={`${f.key}-${idx}`} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div className="text-gray-800">
                      <span className="font-medium">{f.key}</span>
                      <span className="ml-2 text-gray-500">({f.input_type})</span>
                    </div>
                    <button className="text-xs text-red-600 hover:underline" onClick={() => removeField(idx)}>삭제</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="px-4 py-3 flex justify-end gap-2 border-t">
          <button className="px-3 py-2 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={onClose} disabled={saving}>취소</button>
          <button className="px-3 py-2 text-sm rounded text-white bg-gray-800 hover:bg-gray-900 disabled:opacity-50" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  )
}


